import Input from '../../../Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { array, object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'src/services';
import { FRESHDESK_URL } from 'src/constants/urls';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import * as yup from 'yup';
import { IncludeTicketFields } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/IncludeTicketFields/IncludeTicketFields';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';

export const IntegrationFormFreshdeskEmail = ({
  integration,
  onSubmit,
}: {
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { handleSubmit, control, watch, clearErrors } = useForm({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain || '',
      include_custom_fields: false,
      api_key: '',
    },
    resolver: formResolver(
      yup.lazy((values) => {
        return object({
          subdomain: stringScheme({ required: true }).label('Freshdesk subdomain'),
          api_key: stringScheme({ required: true }).label('API key'),
          custom_fields: array(
            object({
              id: stringScheme({ required: values.include_custom_fields }),
              name: stringScheme({ required: values.include_custom_fields }),
            }),
          ),
        });
      }),
    ),
  });

  const submit = (data: {
    subdomain: string;
    include_custom_fields: boolean;
    api_key: string;
    // Added by IncludeTicketFields.
    custom_fields?: { key?: string; id: string; name: string }[];
  }) => {
    const integrationData = {
      ...integration.integration_data,
      name: currentProject.name,
      subdomain: data.subdomain,
      api_key: data.api_key,
      include_custom_fields: data.include_custom_fields,
    };

    if (data.include_custom_fields) {
      integrationData.custom_fields = data.custom_fields.map((field) => {
        return { id: field.id, name: `${field.name}` };
      });
    } else {
      integrationData.custom_fields = null;
    }

    onSubmit({
      ...integration,
      integration_data: integrationData,
    });
  };

  return (
    <form
      id="integration-form"
      className={'flex flex-col gap-2.5 overflow-auto max-h-[60vh] pr-4'}
      onSubmit={handleSubmit(submit)}
    >
      <div className="content flex flex-col gap-1.5">
        <Controller
          control={control}
          name="subdomain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Freshdesk subdomain'}
              placeholder={'domain.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain) {
                  onChange(res?.subdomain);
                } else {
                  onChange(e.target.value);
                }
              }}
              infoTooltip={{
                side: 'right',
                description: 'Link to your Freshdesk workspace',
              }}
            />
          )}
        />
      </div>
      <div className="content flex flex-col gap-1.5">
        <Controller
          control={control}
          name="api_key"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'API key'}
              placeholder={'Yb2KQfS8VQj1HxZlOzVbXk4P6W1Cm3aA1dFxGnYq'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <p>
                    Check how to find the API Key{' '}
                    <a
                      className={'underline'}
                      href={FRESHDESK_URL.WHERE_CAN_FIND_API_KEY}
                      target={'_blank'}
                      rel={'nofollow noreferrer'}
                    >
                      here →
                    </a>
                  </p>
                ),
              }}
            />
          )}
        />
      </div>
      <div className={'mt-5'}>
        <IncludeTicketFields
          control={control}
          watch={watch}
          clearErrors={clearErrors}
          inputFieldType={'text'}
        />
      </div>
    </form>
  );
};
